import { useState, type ReactElement } from 'react';
import { Search } from 'lucide-react';
import { zoneName, type MapResponse, type MapSpot } from '@kratispot/shared';
import { dayMonthOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { listRows, statusFiltersFor, type StatusFilter } from '@/lib/listing';
import { cn } from '@/lib/utils';
import { STATUS_COPY } from './map/spot-tile';

const FILTER_COPY: Readonly<Record<StatusFilter, CopyKey>> = {
  all: 'all',
  free: 'free',
  reserved: 'reserved',
  occupied: 'occupied',
  cleaning: 'cleaning',
};

// The same day the map is showing, read as rows instead of tiles. §15
export function ListView({
  map,
  onOpen,
}: {
  map: MapResponse;
  onOpen: (spot: MapSpot) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const filters = statusFiltersFor(map.mode);
  const chosen = filters.includes(filter) ? filter : 'all';
  const rows = listRows(map, query, chosen);

  return (
    <section className="flex flex-col gap-3 px-2 pb-6 sm:px-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-10 flex-1 items-center gap-2 rounded-xl border bg-background px-3">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            className="w-full bg-transparent text-base outline-none"
            placeholder={t('searchSpots')}
            aria-label={t('searchSpots')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="flex gap-1.5 overflow-x-auto">
          {filters.map((each) => (
            <button
              key={each}
              type="button"
              aria-pressed={chosen === each}
              onClick={() => setFilter(each)}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold',
                chosen === each && 'bg-foreground text-background',
              )}
            >
              {t(FILTER_COPY[each])}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border">
        <table className="w-full text-start text-sm">
          <thead className="bg-secondary text-[11px] tracking-wide uppercase">
            <tr>
              <th className="p-2 text-start">#</th>
              <th className="p-2 text-start">{t('zone')}</th>
              <th className="p-2 text-start">
                {map.mode === 'past' ? t('occupations') : t('status')}
              </th>
              <th className="p-2 text-start">{t('guest')}</th>
              <th className="p-2 text-start">{t('dates')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ spot, booking, guests }) => (
              <tr
                key={spot.id}
                data-testid={`row-${spot.number}`}
                onClick={() => onOpen(spot)}
                className="cursor-pointer border-t hover:bg-secondary/60"
              >
                <td className="p-2 font-bold">#{spot.number}</td>
                <td className="p-2 text-muted-foreground">
                  {zoneName(
                    map.venue.zones.find((zone) => zone.key === spot.zoneKey),
                    language,
                    spot.zoneKey,
                  )}
                  {spot.staffOnly && ' ★'}
                </td>
                <td className="p-2">
                  {spot.status === null ? spot.occupations : t(STATUS_COPY[spot.status])}
                </td>
                <td className="p-2">{guests.length === 0 ? '—' : guests.join(', ')}</td>
                <td className="p-2 whitespace-nowrap text-muted-foreground">
                  {booking === null
                    ? '—'
                    : `${dayMonthOf(booking.startsOn, language)} – ${dayMonthOf(booking.endsOn, language)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('listEmpty')}</p>
        )}
      </div>
    </section>
  );
}
